import clsx from "clsx";
import React, { FC, useState } from "react";
import { useScrollbarWidth } from "react-use";

import { useProductsComparator } from "../../../../contexts/products-comparator";
import { Button } from "../../../buttons/button";
import { Container } from "../../../container";
import { Icon } from "../../../icon";
import { ProductComparatorCard, ProductComparatorCardProps } from "./product-comparator-card";
import { ProductComparatorDetail, ProductComparatorDetailProps } from "./product-comparator-detail";

export interface ProductComparatorPanelProps {
  labels: {
    "product-comparator-compare-btn": string;
    "product-comparator-clear-btn": string;
    "product-comparator-panel-title": string;
  } & ProductComparatorCardProps["labels"] &
    ProductComparatorDetailProps["labels"];
}

export interface VariantsCustomProps {
  headerHeight: number;
}

export const ProductComparatorPanel: FC<ProductComparatorPanelProps> = ({ labels }) => {
  const { selectedProductsCount, products, resetProducts, setPanelOpen, isPanelOpen, maxProducts } =
    useProductsComparator();

  const scrollbarWidth = useScrollbarWidth();
  const [isModalOpen, setModalOpen] = useState(false);

  const buttons = (
    <>
      <Button
        variant="primary"
        size="md"
        disabled={selectedProductsCount < 2}
        onClick={() => setModalOpen(true)}
      >
        {labels["product-comparator-compare-btn"]}
      </Button>
      <Button variant="primary-outline" size="md" onClick={resetProducts}>
        {labels["product-comparator-clear-btn"]}
      </Button>
    </>
  );

  const productsToCompare = products.map((product, i) => {
    return <ProductComparatorCard labels={labels} product={product} key={i} />;
  });

  return (
    <>
      <div className="fixed bottom-0 w-screen z-40" style={{ paddingRight: scrollbarWidth }}>
        <Container
          containerFrom="lg"
          containerClassName={clsx(
            "max-w-7xl transition-all duration-300 relative",
            selectedProductsCount === 0 ? "max-h-0" : isPanelOpen ? "max-h-[90vh]" : "max-h-0"
          )}
        >
          <button
            type="button"
            onClick={() => setPanelOpen((isOpen) => !isOpen)}
            className={clsx(
              "absolute top-[1px] w-fit lg:w-full transition-all duration-300",
              "rounded-t-3xl lg:rounded-t-4xl bg-primary-light",
              "overflow-hidden p-4 flex justify-center",
              "border-2 border-light",
              selectedProductsCount > 0 ? "-translate-y-full" : "translate-y-0",
              "flex items-center gap-1 justify-center font-bold text-sm md:text-base lg:text-xl"
            )}
          >
            {labels["product-comparator-panel-title"]} ({selectedProductsCount}/{maxProducts})
            <Icon
              icon="chevron-up-thick"
              className={clsx(
                "w-4 transition-all duration-300 ml-1",
                isPanelOpen && "rotate-180 mt-1"
              )}
            />
          </button>

          <div className="bg-light border-0 w-full pb-20 lg:pb-0 max-lg:shadow-[0_0_18px_0px_rgba(0,0,0,0.2)]">
            {/* DESKTOP VERSION */}
            <div className="hidden lg:flex gap-5 p-6 w-full h-[172px]">
              {productsToCompare}
              <div className="flex flex-none flex-col justify-between gap-4">{buttons}</div>
            </div>
            {/* MOBILE VERSION */}
            <div className="lg:hidden flex flex-col gap-5 p-3 w-full">
              <div className="flex gap-2 pt-5">{productsToCompare}</div>
              <div className="flex flex-none flex-col justify-between gap-4">{buttons}</div>
            </div>
          </div>
        </Container>
      </div>
      <ProductComparatorDetail
        labels={labels}
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
